import React, { ReactNode, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    OptionConfiguratorContext,
    OptionConfiguratorContextType,
} from '@/components/OptionConfigurator/OptionConfiguratorContext';
import { getShowroomData, ShowroomPathParams, showroomKeys } from '@/services/showroom';
import { BuyerProjectData, ProjectDecimalOption, UnitOfMeasurement } from '@/types';
import { BygrModule } from '@ygtechnologies/bygr-frontend-component-library';
import { BuyerRoomDto } from '@/services/rooms';
import { useConfiguratorVisualization } from '@/services/configurator-visualization';
import { groupConfiguratorRooms } from '@/components/OptionConfigurator/utils/configuratorRooms';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { ShowroomMutationProvider } from './ShowroomMutationProvider';

interface ShowroomConfiguratorProviderProps {
    projectSlug: string;
    unitId: number;
    hash: string;
    basePath: string;
    baseUrl: string;
    initialDeadlineId?: number;
    children: ReactNode;
}

// Adapts the public showroom API to the OptionConfiguratorContext interface so that the existing
// configurator components (Room, BuyerOptionCard, RoomStyleCard, BottomPanel, etc.) can be reused
// without modification. Pairs with ShowroomMutationProvider for stateless option/style picking.
export const ShowroomConfiguratorProvider: React.FC<ShowroomConfiguratorProviderProps> = ({
    projectSlug,
    unitId,
    hash,
    basePath,
    baseUrl,
    initialDeadlineId,
    children,
}) => {
    const pathParams: ShowroomPathParams = useMemo(() => ({ projectSlug, unitId, hash }), [projectSlug, unitId, hash]);
    const showroomPath = `${projectSlug}/${unitId}/${hash}`;

    const { data, isLoading, error } = useQuery({
        queryKey: showroomKeys.byUnit(unitId, hash),
        queryFn: () => getShowroomData(pathParams),
        retry: 0, // 404s shouldn't retry
        // The showroom config is static for a session. The applied selection state lives in this
        // same cache entry (patched by the replay effect + mutations in ShowroomMutationProvider),
        // and the replay only runs once — so a refetch of the bare GET would overwrite the applied
        // state with the default (unselected) data and never re-apply it. Never auto-refetch.
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
    });

    // Fetch the unit's visualization data up-front (through the anonymous showroom endpoint) so a room
    // renders straight into its visual panel instead of flashing the list view while the data loads.
    const { data: visualizationRooms, isLoading: visualizationLoading } =
        useConfiguratorVisualization(unitId, showroomPath);

    const [is3d, setIs3d] = useState(false);
    const [tabsHeight, setTabsHeight] = useState(273);
    const [selectedDeadlineId, setSelectedDeadlineId] = useState<number | null>(initialDeadlineId ?? null);
    const [tabsEnabled, setTabsEnabled] = useState(true);
    const [bottomPanelEnabled, setBottomPanelEnabled] = useState(true);
    const [viewOnly, setViewOnly] = useState(false);

    // Default to the first deadline once data lands.
    React.useEffect(() => {
        if (selectedDeadlineId == null && data?.deadlines.length) {
            setSelectedDeadlineId(data.deadlines[0].id);
        }
    }, [data, selectedDeadlineId]);

    const projectData = useMemo<BuyerProjectData | null>(() => {
        if (!data) return null;
        return {
            projectId: 0, // unknown to the public showroom — components should not key behavior off this
            projectName: data.metadata.projectName ?? '',
            unitName: data.metadata.unitName ?? '',
            address: '',
            floor: '',
            unitType: 'Apartment',
            isInReadOnlyMode: false,
            sellerProfileId: 0,
            sellerProfileName: '',
            features: null,
            changeRequestWarningText: null,
            changeRequestDeadline: null,
            currencyCode: data.metadata.currencyCode ?? 'NOK',
            sellerModules: [BygrModule.ChangeManagement],
            buyerModules: [BygrModule.ChangeManagement],
            useWhiteLabel: false,
            showBackgroundImageOnMessages: false,
            defaultUnitOfMeasurement: data.metadata.defaultUnitOfMeasurement ?? UnitOfMeasurement.Centimeter,
            optionsTerms: '',
            showDefaultTextOnOptions: true,
            useLegacyOptionsView: data.metadata.useLegacyOptionsView ?? false,
            useRooms: data.metadata.useRooms ?? false,
            decimalOption: data.metadata.decimalOption ?? ProjectDecimalOption.None,
            deadlines: data.deadlines.map((d) => ({
                id: d.id,
                projectId: 0,
                name: d.name,
                deadline: d.deadline,
                description: '',
                isChangeRequestDeadline: false,
                hasOptionsAttached: true,
                awaitingSignatures: 0,
                totalPrice: 0,
            })),
        } as BuyerProjectData;
    }, [data]);

    // Room metadata arrives in the showroom payload (not the authenticated buyer rooms endpoint).
    // Build the same Map<id, BuyerRoomDto> the auth provider gets from useBuyerRoomMap, then derive
    // the room-grouped views the configurator components read from context. The showroom serves a
    // single (all-deadlines) configuratorData, so `rooms` and `allDeadlineRooms` group the same set.
    const roomsMap = useMemo<Map<number, BuyerRoomDto>>(() => {
        const map = new Map<number, BuyerRoomDto>();
        (data?.rooms ?? []).forEach((r) =>
            map.set(r.id, {
                id: r.id,
                sortOrder: r.sortOrder,
                name: r.name,
                buyerDescription: r.buyerDescription,
                icon: r.icon as IconName,
            })
        );
        return map;
    }, [data]);

    const rooms = useMemo(() => groupConfiguratorRooms(data?.configuratorData?.groups ?? [], roomsMap), [data, roomsMap]);
    const getRoomMeta = React.useCallback((id: number) => roomsMap.get(id), [roomsMap]);

    const value = useMemo<OptionConfiguratorContextType>(
        () => ({
            is3d,
            setIs3d,
            tabsHeight,
            setTabsHeight,
            selectedDeadlineId,
            setSelectedDeadlineId,
            configuratorData: data?.configuratorData,
            allDeadlinesConfiguratorData: data?.configuratorData,
            rooms,
            allDeadlineRooms: rooms,
            getRoomMeta,
            visualizationRooms,
            visualizationLoading,
            projectData,
            basePath,
            baseUrl,
            // The unit id is part of the URL. Existing reused components don't actually call
            // unit-scoped mutations through this field after the Phase 3 refactor; the few that
            // still read it (DependencyWizardModal, OptionConfiguratorTabs) are not used by the
            // showroom UI shell. Setting it anyway is harmless.
            residentialUnitId: unitId,
            tabsEnabled,
            setTabsEnabled,
            bottomPanelEnabled,
            setBottomPanelEnabled,
            viewOnly,
            setViewOnly,
            // The public showroom has no pricing-field metadata, so option-group quantity units
            // (e.g. "per m²") are unavailable — price suffixes are simply omitted.
            getOptionGroupQuantityUnit: () => null,
            isShowroom: true,
            immersive: true,
            // The showroom already serves a single all-deadlines payload, so the unified multi-
            // deadline view (per-deadline menu sections) is always on here; no refetch is needed.
            multiDeadline: true,
            showroomPath,
        }),
        [
            is3d,
            tabsHeight,
            selectedDeadlineId,
            data,
            projectData,
            basePath,
            baseUrl,
            tabsEnabled,
            bottomPanelEnabled,
            viewOnly,
            rooms,
            getRoomMeta,
            visualizationRooms,
            visualizationLoading,
            unitId,
            showroomPath,
        ]
    );

    if (isLoading) {
        return <div style={{ padding: 24, textAlign: 'center' }}>Loading…</div>;
    }
    if (error || !data) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                <h2>Showroom not found</h2>
                <p>The link may have been disabled by the seller, or the URL is incorrect.</p>
            </div>
        );
    }

    return (
        <OptionConfiguratorContext.Provider value={value}>
            <ShowroomMutationProvider pathParams={pathParams}>{children}</ShowroomMutationProvider>
        </OptionConfiguratorContext.Provider>
    );
};
